import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, RefreshCw, AlertCircle, FileText, ExternalLink } from 'lucide-react';
import { getSharedInvoice, getSharedInvoicePdf, downloadPDF } from '../services/api';
import { formatCurrency } from '../utils/currencyFormatter';

function formatDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  return isNaN(d) ? str : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

const STATUS_STYLES = {
  paid:    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  unpaid:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
};

export default function SharedInvoicePage() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    getSharedInvoice(token)
      .then(data => { if (active) setInvoice(data); })
      .catch(err => { if (active) setError(err.message || 'Invoice not found.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await getSharedInvoicePdf(token);
      downloadPDF(blob, filename);
    } catch {
      /* ignore */
    } finally {
      setDownloading(false);
    }
  }, [token]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  // ── Error / not found ─────────────────────────────────────────────────────
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center gap-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">Invoice Not Found</h1>
        <p className="text-gray-500 text-sm max-w-sm">
          This link may have expired or is invalid. Ask the sender to share it again.
        </p>
        <Link to="/" className="mt-2 text-sm text-blue-600 hover:underline flex items-center gap-1">
          Create your own invoice free <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  const status = invoice.payment_status || 'unpaid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between max-w-2xl mx-auto">
        <img src="/2.png" alt="Invilic" className="h-14 w-auto" />
        <Link to="/" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          Create free invoice <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Card */}
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

          {/* Header band */}
          <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-8 py-7">
            <p className="text-xs font-semibold text-blue-200 uppercase tracking-widest mb-1">Invoice from</p>
            <h1 className="text-2xl font-bold text-white">{invoice.business_name || 'Your Business'}</h1>
          </div>

          {/* Body */}
          <div className="px-8 py-7 space-y-6">

            {/* Invoice meta row */}
            <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Invoice #</p>
                <p className="text-base font-bold text-gray-900 mt-0.5">{invoice.invoice_number}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Billed to</p>
                <p className="text-base font-medium text-gray-800 mt-0.5">{invoice.client_name || '-'}</p>
              </div>
              <div className="ml-auto">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.unpaid}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Invoice Date</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">{formatDate(invoice.invoice_date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Due Date</p>
                <p className={`text-sm font-semibold mt-1 ${status === 'overdue' ? 'text-red-600' : 'text-gray-800'}`}>
                  {formatDate(invoice.due_date)}
                </p>
              </div>
            </div>

            {/* Amount due */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-6 py-5 text-center">
              <p className="text-xs font-semibold text-blue-500 uppercase tracking-widest mb-1">Total Amount</p>
              <p className="text-4xl font-black text-blue-700 tracking-tight">
                {formatCurrency(invoice.total, invoice.currency)}
              </p>
              {invoice.discount > 0 && (
                <p className="text-xs text-blue-400 mt-1">
                  Includes {formatCurrency(invoice.discount, invoice.currency)} discount
                </p>
              )}
            </div>

            {/* Download button */}
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl transition-colors shadow-md shadow-blue-600/20"
            >
              {downloading
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Preparing PDF…</>
                : <><Download className="w-4 h-4" /> Download Invoice PDF</>}
            </button>

            {/* PDF note */}
            <div className="flex items-center gap-2.5 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
              <FileText className="w-4 h-4 text-green-500 flex-shrink-0" />
              <p className="text-xs text-green-700">
                <span className="font-semibold">{invoice.invoice_number}.pdf</span>. Tap Download to save a copy.
              </p>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="border-t border-gray-100 bg-gray-50 px-8 py-5 text-center">
            <p className="text-xs text-gray-400 mb-2">Sent via</p>
            <img src="/2.png" alt="Invilic" className="h-14 w-auto mx-auto mb-3" />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline"
            >
              Create your free invoice, no credit card needed
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
